/**
 * Client-portal navigation — role-scoped IA (Klaret).
 *
 * The portal serves two non-overlapping roles off one shell: the **client**
 * (Dashboard · Approvals · Chat · Account) and the workspace **sales rep**
 * (`client_salesman`: My leads · Account). The shell picks the nav by role via
 * `navItemsForRole`; rep screens S6–S9 (Availability/Booking/Calendar) are a
 * later increment and are deliberately NOT navved (no dead nav items).
 *
 * Icons: NAV_ICONS line/fill pairs from @teivor/shared-ui (MingCute — no Lucide).
 */
import { NAV_ICONS } from '@teivor/shared-ui';
import type { NavGlyph } from '@teivor/shared-ui';

// Re-export NavGlyph so consumers (AppLayout) can type icon props.
export type { NavGlyph };

export interface PortalNavItem {
  id: string;
  /** React Router path */
  to: string;
  /** i18n key */
  labelKey: string;
  /** MingCute line/fill pair */
  glyph: NavGlyph;
  /** Exact-match active detection (root only) */
  end?: boolean;
}

/** Client (`client`) IA. */
export const CLIENT_NAV_ITEMS: PortalNavItem[] = [
  { id: 'dashboard', to: '/', labelKey: 'client.nav.dashboard', glyph: NAV_ICONS.dashboard, end: true },
  { id: 'approvals', to: '/approvals', labelKey: 'client.nav.approvals', glyph: NAV_ICONS.approvals },
  { id: 'chat', to: '/chat', labelKey: 'client.nav.chat', glyph: NAV_ICONS.chat },
  { id: 'profile', to: '/profile', labelKey: 'client.nav.profile', glyph: NAV_ICONS.account },
];

/** Workspace sales rep (`client_salesman`) IA — My leads + the shared Account page. */
export const REP_NAV_ITEMS: PortalNavItem[] = [
  { id: 'leads', to: '/leads', labelKey: 'client.nav.myLeads', glyph: NAV_ICONS.myLeads },
  { id: 'profile', to: '/profile', labelKey: 'client.nav.profile', glyph: NAV_ICONS.account },
];

/** Pick the nav for the signed-in role. Reps get the rep IA; everyone else the client IA. */
export function navItemsForRole(role: string | undefined): PortalNavItem[] {
  return role === 'client_salesman' ? REP_NAV_ITEMS : CLIENT_NAV_ITEMS;
}

/** Is this nav item active for the given pathname? */
export function isNavItemActive(item: PortalNavItem, pathname: string): boolean {
  if (item.end || item.to === '/') return pathname === item.to;
  return pathname === item.to || pathname.startsWith(`${item.to}/`);
}

/** Deepest nav item (within `items`) whose route matches `pathname` (or the first item). */
export function activeNavItem(items: PortalNavItem[], pathname: string): PortalNavItem {
  const root = items[0]!;
  if (pathname === root.to) return root;
  return (
    [...items]
      .filter((i) => i.to !== '/')
      .sort((a, b) => b.to.length - a.to.length)
      .find((i) => pathname === i.to || pathname.startsWith(`${i.to}/`)) ?? root
  );
}
